// /src/pdf/builders/aha/buildDoc.js
export function buildAhaDoc({
  t,
  shortcode,
  stationName,
  serviceName,
  matrix = [],
  hours = [],
  meta = null,
  fmtNumber,
  locale,
  viewMode,
  chartDataUrl = null
}) {
  const serviceTitle = serviceName || (t && t('aha.title')) || 'Average daily listening profile by weekday';
  const stationTitle = stationName || shortcode || '';

  const endDate = meta?.period?.end_date || '';
  const months = meta?.period?.months;
  const timezone = meta?.tz;

  const weekdayOrder = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  const weekdayLabels = weekdayOrder.map((key, idx) => {
    const label = t && t(`aha.weekdays.${key}`);
    if (label && label !== `aha.weekdays.${key}`) return label;
    const fallback = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][idx];
    return fallback;
  });

  const hourLabels = Array.isArray(hours) && hours.length
    ? hours.map((h) => String(h).padStart(2, '0'))
    : Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));

  const formatNumber = (value) => {
    const num = Number(value ?? 0);
    if (Number.isFinite(num)) {
      if (typeof fmtNumber === 'function') {
        return fmtNumber(num, locale);
      }
      return num.toLocaleString(locale || 'en-US');
    }
    return '0';
  };

  const detailsRows = (matrix || []).map((row = [], idx) => {
    const label = weekdayLabels[idx] || `D${idx + 1}`;
    const payload = hourLabels
      .map((hour, hIdx) => `${hour}: ${formatNumber(row[hIdx])}`)
      .join('   ');
    return [
      { text: label, alignment: 'left' },
      { text: payload, alignment: 'left', fontSize: 9 }
    ];
  });

  const flatValues = (matrix || []).flat().map((v) => Number(v)).filter((n) => Number.isFinite(n) && n > 0);
  const minVal = flatValues.length ? Math.min(...flatValues) : 0;
  const maxVal = flatValues.length ? Math.max(...flatValues) : 0;
  const avgVal = flatValues.length
    ? flatValues.reduce((acc, val) => acc + val, 0) / flatValues.length
    : 0;

  const infoLines = [
    endDate ? `${t ? t('aha.end_date') : 'Period end date'}: ${endDate}` : null,
    months ? `${t ? t('aha.depth') : 'Depth'}: ${months}` : null,
    timezone ? `${t ? t('aha.timezone', 'Time zone') : 'Time zone'}: ${timezone}` : null,
  ].filter(Boolean);

  const methodLine = (t && t('aha.method_legend'))
    || 'Method: average per minute points → average per hour → average of identical slots (weekday, hour).';

  const summaryRows = [
    [
      { text: (t && t('aha.legend.min')) || 'Minimum', alignment: 'left', style: 'th' },
      { text: formatNumber(minVal), alignment: 'right' }
    ],
    [
      { text: (t && t('aha.legend.max')) || 'Maximum', alignment: 'left', style: 'th' },
      { text: formatNumber(maxVal), alignment: 'right' }
    ],
    [
      { text: (t && t('aha.legend.avg')) || 'Average', alignment: 'left', style: 'th' },
      { text: formatNumber(avgVal), alignment: 'right' }
    ],
  ];

  const content = [];

  content.push(
    { text: serviceTitle, style: 'h1', margin: [0, 0, 0, 2] }
  );

  if (stationTitle) {
    content.push({ text: stationTitle, style: 'h2', margin: [0, 0, 0, 6] });
  }

  if (infoLines.length) {
    content.push({
      margin: [0, 0, 0, 8],
      fontSize: 10,
      color: '#555',
      stack: infoLines
    });
  }

  if (viewMode === 'lines' && chartDataUrl) {
    content.push({
      image: chartDataUrl,
      width: 520,
      margin: [0, 0, 0, 10]
    });
  }

  content.push({
    table: {
      headerRows: 1,
      widths: ['auto', '*'],
      body: [
        [
          { text: (t && t('aha.view.weekday', 'Weekday')) || 'Weekday', style: 'th', alignment: 'left' },
          { text: (t && t('aha.chart.hourly', 'Hourly values')) || 'Hourly values', style: 'th', alignment: 'left' }
        ],
        ...detailsRows
      ]
    },
    layout: {
      fillColor: (rowIndex) => (rowIndex === 0 ? '#f0f0f8' : null),
      hLineColor: '#d7dbef',
      vLineColor: '#d7dbef',
    },
    margin: [0, 0, 0, 10]
  });

  content.push({
    table: {
      widths: ['*', 'auto'],
      body: summaryRows
    },
    layout: {
      fillColor: (rowIndex) => (rowIndex === 0 ? '#f7f9ff' : null),
      hLineColor: '#d7dbef',
      vLineColor: '#d7dbef',
    },
    margin: [0, 0, 0, 8]
  });

  if (methodLine) {
    content.push({
      text: methodLine,
      fontSize: 9,
      color: '#777'
    });
  }

  return {
    pageSize: 'A4',
    pageMargins: [26, 32, 26, 40],
    content,
    footer: (currentPage, pageCount) => ({
      margin: [26, 0, 26, 12],
      fontSize: 9,
      columns: [
        { text: `${serviceTitle}`, alignment: 'left' },
        { text: `${currentPage} / ${pageCount}`, alignment: 'right' }
      ]
    }),
    styles: {
      h1: { fontSize: 18, bold: true },
      h2: { fontSize: 14, bold: true },
      th: { bold: true }
    }
  };
}

export { buildAhaDoc as default };
